import React, { useCallback, useEffect, useState } from "react";
import AdminLayout from "../../../layouts/AdminLayout";
import Subheader from "../../../components/ui/Subheader";
import {
  Button,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Select,
  Space,
} from "antd";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import IconButton from "../../../components/common/IconButton";
import LeftArrowIcon from "../../../assets/icons/LeftArrowIcon";
import {
  RootState,
  useAppDispatch,
  useAppSelector,
} from "../../../store/store";
import {
  ArrayOption,
  pathLink,
  selectSearchOption,
} from "../../../helpers/siteConfig";
import { GetSinglePriceMatrixParams } from "../../../types/redux";
import {
  getSinglePriceMatrixAsync,
  updatePriceMatrixAsync,
} from "../../../store/features/NP/payment/paymentAPI";
import {
  getAllAreaAsync,
  getAllRegionAsync,
} from "../../../store/features/NP/geoInfo/geoAPI";
import dayjs from "dayjs";
import { capitalize } from "../../../utils/string";
import Loader from "../../../components/common/Loader";

export default function PriceMatrixEdit() {
  const [regionId, setRegionId] = useState<string | undefined>(undefined);
  const [areaId, setAreaId] = useState<string | undefined>(undefined);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const [form] = Form.useForm();

  const { login } = useAppSelector((state) => state.login);
  const { regions, areas } = useAppSelector((state) => state.npGeo);
  const { singlePriceMatrix, singlePriceMatrixLoading, updateLoading } =
    useAppSelector((state: RootState) => state.payment);

  useEffect(() => {
    dispatch(getAllRegionAsync({}));
  }, [dispatch]);

  useEffect(() => {
    if (regionId)
      dispatch(
        getAllAreaAsync({
          params: {
            region: regionId,
          },
        }),
      );
  }, [dispatch, regionId]);

  const priceMatrixData = useCallback(() => {
    const params: GetSinglePriceMatrixParams = {
      id: id,
    };

    dispatch(
      getSinglePriceMatrixAsync({
        params,
      }),
    );
  }, [dispatch, id]);

  useEffect(() => {
    priceMatrixData();
  }, [priceMatrixData]);

  const reRender = () => {
    priceMatrixData();
    navigate(-1);
  };

  useEffect(() => {
    if (id && singlePriceMatrix) {
      form.setFieldsValue({
        cluster: singlePriceMatrix.cluster?._id,
        zone: singlePriceMatrix.zone?._id,
        vendor: singlePriceMatrix.vendor?._id || "",
        vendorId: singlePriceMatrix.vendorId || "",
        effectiveDate: singlePriceMatrix.effectiveDate
          ? dayjs(singlePriceMatrix.effectiveDate)
          : null,
        standardMissedCallLineItem:
          singlePriceMatrix.standardMissedCallLineItem || "",
        standardMissedCallAmount:
          singlePriceMatrix.standardMissedCallAmount ?? 0,
        standardValidTTSinglePGLineItem:
          singlePriceMatrix.standardValidTTSinglePGLineItem || "",
        standardValidTTSinglePGAmount:
          singlePriceMatrix.standardValidTTSinglePGAmount ?? 0,
        standardValidTTDoublePGLineItem:
          singlePriceMatrix.standardValidTTDoublePGLineItem || "",
        standardValidTTDoublePGAmount:
          singlePriceMatrix.standardValidTTDoublePGAmount ?? 0,
        standardPGRHLineItem: singlePriceMatrix.standardPGRHLineItem || "",
        standardPGRHAmount: singlePriceMatrix.standardPGRHAmount ?? 0,
        premiumMissedCallLineItem:
          singlePriceMatrix.premiumMissedCallLineItem || "",
        premiumMissedCallAmount: singlePriceMatrix.premiumMissedCallAmount ?? 0,
        premiumValidTTSinglePGLineItem:
          singlePriceMatrix.premiumValidTTSinglePGLineItem || "",
        premiumValidTTSinglePGAmount:
          singlePriceMatrix.premiumValidTTSinglePGAmount ?? 0,
        premiumValidTTDoublePGLineItem:
          singlePriceMatrix.premiumValidTTDoublePGLineItem || "",
        premiumValidTTDoublePGAmount:
          singlePriceMatrix.premiumValidTTDoublePGAmount ?? 0,
        premiumPGRHLineItem: singlePriceMatrix.premiumPGRHLineItem || "",
        premiumPGRHAmount: singlePriceMatrix.premiumPGRHAmount ?? 0,
        contingencyMissedCallLineItem:
          singlePriceMatrix.contingencyMissedCallLineItem || "",
        contingencyMissedCallAmount:
          singlePriceMatrix.contingencyMissedCallAmount ?? 0,
        contingencyValidTTSinglePGLineItem:
          singlePriceMatrix.contingencyValidTTSinglePGLineItem || "",
        contingencyValidTTSinglePGAmount:
          singlePriceMatrix.contingencyValidTTSinglePGAmount ?? 0,
        contingencyValidTTDoublePGLineItem:
          singlePriceMatrix.contingencyValidTTDoublePGLineItem || "",
        contingencyValidTTDoublePGAmount:
          singlePriceMatrix.contingencyValidTTDoublePGAmount ?? 0,
        contingencyPGRHLineItem:
          singlePriceMatrix.contingencyPGRHLineItem || "",
        contingencyPGRHAmount: singlePriceMatrix.contingencyPGRHAmount ?? 0,
      });

      setRegionId(singlePriceMatrix.cluster?._id);
      setAreaId(singlePriceMatrix.zone?._id);
    }
  }, [form, id, singlePriceMatrix]);

  const onFinish = (values: any) => {
    const newData = {
      ...values,
      effectiveDate: values.effectiveDate
        ? dayjs(values.effectiveDate).utc().startOf("day").toISOString()
        : null,
    };

    if (id) {
      dispatch(
        updatePriceMatrixAsync({
          data: newData,
          others: {
            reRender,
            id: id,
          },
        }),
      );
    }
  };

  return (
    <>
      <AdminLayout title={"Price Matrix"} headerTitle="Price Matrix">
        <section className={`shadow-sm bg-white rounded-lg mt-7 pb-7`}>
          <Subheader title={"Edit Price Matrix"}>
            <Space size="middle">
              <IconButton
                className="!border-others-header !text-sm !font-normal !rounded-lg !border !text-header !bg-transparent"
                type="default"
                icon={<LeftArrowIcon />}
                onClick={() => navigate(-1)}
              >
                <span>Back</span>
              </IconButton>
            </Space>
          </Subheader>

          {singlePriceMatrixLoading ? (
            <Loader />
          ) : (
            <div className="w-full mx-auto px-6 pt-14 min-h-[400px]">
              <Form
                className="custom-form hide_star_mark"
                layout="vertical"
                onFinish={onFinish}
                size="large"
                form={form}
              >
                <div className="flex items-center gap-4 mb-5">
                  <div className="text-slate-600 text-lg font-semibold">
                    General Information
                  </div>
                  <div className="flex-1 h-px bg-zinc-200"></div>
                </div>

                <div className="grid grid-cols-2 items-start gap-x-6 gap-y-0">
                  <Form.Item
                    name="cluster"
                    label="Cluster Name"
                    rules={[
                      { required: true, message: "Please select cluster" },
                    ]}
                  >
                    <Select
                      placeholder="Select cluster"
                      size="large"
                      allowClear
                      showSearch
                      filterOption={selectSearchOption}
                      options={
                        regions?.map((r) => ({
                          value: r._id,
                          label: capitalize(r.regionName),
                        })) || []
                      }
                      onChange={(val: string) => {
                        setRegionId(val);
                        setAreaId(undefined);
                        form.setFieldValue("zone", undefined);
                      }}
                      disabled
                    />
                  </Form.Item>

                  <Form.Item
                    name="zone"
                    label="Zone Name"
                    rules={[{ required: true, message: "Please select zonal" }]}
                  >
                    <Select
                      placeholder="Select zonal"
                      size="large"
                      allowClear
                      showSearch
                      filterOption={selectSearchOption}
                      options={
                        areas?.map((a) => ({
                          value: a._id,
                          label: capitalize(a.areaName),
                        })) || []
                      }
                      value={areaId}
                      onChange={(val: string) => setAreaId(val)}
                      disabled
                    />
                  </Form.Item>

                  {/* <Form.Item
                    name="vendor"
                    label="Vendor"
                    rules={[
                      {
                        required: true,
                        message: "Please enter operator first name",
                      },
                    ]}
                  >
                    <Input
                      size="large"
                      placeholder="Enter first name"
                      allowClear
                    />
                  </Form.Item> */}
                  <Form.Item
                    name="vendorId"
                    label="Vendor ID"
                    rules={[
                      {
                        required: true,
                        message: "Please enter operator first name",
                      },
                    ]}
                  >
                    <Input
                      size="large"
                      placeholder="Enter first name"
                      allowClear
                      disabled
                    />
                  </Form.Item>

                  {/* <Form.Item
                    name="effectiveDate"
                    label="Effective Date"
                    rules={[
                      {
                        required: true,
                        message: "Please enter operator first name",
                      },
                    ]}
                  >
                    <DatePicker
                      size="large"
                      allowClear
                      style={{ width: "100%" }}
                      disabled
                    />
                  </Form.Item> */}
                </div>

                <div className="flex items-center gap-4 mb-5">
                  <div className="text-slate-600 text-lg font-semibold">
                    Standard Information
                  </div>
                  <div className="flex-1 h-px bg-zinc-200"></div>
                </div>

                <div className="grid grid-cols-2 items-start gap-x-6 gap-y-0">
                  <Form.Item
                    name="standardMissedCallLineItem"
                    label="Standard_Missed Call Std_Line Item # 1"
                    rules={[
                      {
                        required: true,
                        message: "Please enter Standard Missed Call Line Item",
                      },
                    ]}
                  >
                    <Input
                      size="large"
                      placeholder="Enter Line Item"
                      allowClear
                    />
                  </Form.Item>

                  <Form.Item
                    name="standardMissedCallAmount"
                    label="Standard_Missed Call Std_Missed Call Amount"
                    rules={[
                      {
                        required: true,
                        message: "Please enter Standard Missed Call Amount",
                      },
                      {
                        validator: (_, value) => {
                          if (value === undefined || value === null)
                            return Promise.resolve();
                          // Check if it has more than 2 decimals
                          const decimalPart = value.toString().split(".")[1];
                          if (decimalPart && decimalPart.length > 2) {
                            return Promise.reject(
                              new Error(
                                "Only up to 2 decimal places are allowed",
                              ),
                            );
                          }
                          return Promise.resolve();
                        },
                      },
                    ]}
                  >
                    <InputNumber
                      size="large"
                      placeholder="Enter Amount"
                      className="w-full"
                    />
                  </Form.Item>

                  <Form.Item
                    name="standardValidTTSinglePGLineItem"
                    label="Standard_Valid TT_Single PG Std_Line Item # 2"
                    rules={[
                      {
                        required: true,
                        message:
                          "Please enter Standard Valid TT Single PG Line Item",
                      },
                    ]}
                  >
                    <Input
                      size="large"
                      placeholder="Enter Line Item"
                      allowClear
                    />
                  </Form.Item>

                  <Form.Item
                    name="standardValidTTSinglePGAmount"
                    label="Standard_Valid TT_Single PG Std_Valid TT Amount"
                    rules={[
                      {
                        required: true,
                        message:
                          "Please enter Standard Valid TT Single PG Amount",
                      },
                      {
                        validator: (_, value) => {
                          if (value === undefined || value === null)
                            return Promise.resolve();
                          // Check if it has more than 2 decimals
                          const decimalPart = value.toString().split(".")[1];
                          if (decimalPart && decimalPart.length > 2) {
                            return Promise.reject(
                              new Error(
                                "Only up to 2 decimal places are allowed",
                              ),
                            );
                          }
                          return Promise.resolve();
                        },
                      },
                    ]}
                  >
                    <InputNumber
                      size="large"
                      placeholder="Enter Amount"
                      className="w-full"
                    />
                  </Form.Item>

                  <Form.Item
                    name="standardValidTTDoublePGLineItem"
                    label="Standard_Valid TT_Double PG Std_Line Item # 3"
                    rules={[
                      {
                        required: true,
                        message:
                          "Please enter Standard Valid TT Double PG Line Item",
                      },
                    ]}
                  >
                    <Input
                      size="large"
                      placeholder="Enter Line Item"
                      allowClear
                    />
                  </Form.Item>

                  <Form.Item
                    name="standardValidTTDoublePGAmount"
                    label="Standard_Valid TT_Double PG Std_Valid TT Amount"
                    rules={[
                      {
                        required: true,
                        message:
                          "Please enter Standard Valid TT Double PG Amount",
                      },
                      {
                        validator: (_, value) => {
                          if (value === undefined || value === null)
                            return Promise.resolve();
                          // Check if it has more than 2 decimals
                          const decimalPart = value.toString().split(".")[1];
                          if (decimalPart && decimalPart.length > 2) {
                            return Promise.reject(
                              new Error(
                                "Only up to 2 decimal places are allowed",
                              ),
                            );
                          }
                          return Promise.resolve();
                        },
                      },
                    ]}
                  >
                    <Input size="large" placeholder="Enter Amount" allowClear />
                  </Form.Item>

                  <Form.Item
                    name="standardPGRHLineItem"
                    label="Standard_PGRH Std_Line Item # 4"
                    rules={[
                      {
                        required: true,
                        message: "Please enter Standard PGRH Line Item",
                      },
                    ]}
                  >
                    <Input
                      size="large"
                      placeholder="Enter Line Item"
                      allowClear
                    />
                  </Form.Item>

                  <Form.Item
                    name="standardPGRHAmount"
                    label="Standard_PGRH Std_PGRH Amount"
                    rules={[
                      {
                        required: true,
                        message: "Please enter Standard PGRH Amount",
                      },
                      {
                        validator: (_, value) => {
                          if (value === undefined || value === null)
                            return Promise.resolve();
                          // Check if it has more than 2 decimals
                          const decimalPart = value.toString().split(".")[1];
                          if (decimalPart && decimalPart.length > 2) {
                            return Promise.reject(
                              new Error(
                                "Only up to 2 decimal places are allowed",
                              ),
                            );
                          }
                          return Promise.resolve();
                        },
                      },
                    ]}
                  >
                    <InputNumber
                      size="large"
                      placeholder="Enter Amount"
                      className="w-full"
                    />
                  </Form.Item>
                </div>

                <div className="flex items-center gap-4 mb-5">
                  <div className="text-slate-600 text-lg font-semibold">
                    Premium Information
                  </div>
                  <div className="flex-1 h-px bg-zinc-200"></div>
                </div>

                <div className="grid grid-cols-2 items-start gap-x-6 gap-y-0">
                  <Form.Item
                    name="premiumMissedCallLineItem"
                    label="Premium_Missed Call Prm_Line Item # 1"
                    rules={[
                      {
                        required: true,
                        message: "Please enter Premium Missed Call Line Item",
                      },
                    ]}
                  >
                    <Input
                      size="large"
                      placeholder="Enter Line Item"
                      allowClear
                    />
                  </Form.Item>

                  <Form.Item
                    name="premiumMissedCallAmount"
                    label="Premium_Missed Call Prm_Missed Call Amount"
                    rules={[
                      {
                        required: true,
                        message: "Please enter Premium Missed Call Amount",
                      },
                      {
                        validator: (_, value) => {
                          if (value === undefined || value === null)
                            return Promise.resolve();
                          // Check if it has more than 2 decimals
                          const decimalPart = value.toString().split(".")[1];
                          if (decimalPart && decimalPart.length > 2) {
                            return Promise.reject(
                              new Error(
                                "Only up to 2 decimal places are allowed",
                              ),
                            );
                          }
                          return Promise.resolve();
                        },
                      },
                    ]}
                  >
                    <InputNumber
                      size="large"
                      placeholder="Enter Amount"
                      className="w-full"
                    />
                  </Form.Item>

                  <Form.Item
                    name="premiumValidTTSinglePGLineItem"
                    label="Premium_Valid TT_Single PG Prm_Line Item # 2"
                    rules={[
                      {
                        required: true,
                        message:
                          "Please enter Premium Valid TT Single PG Line Item",
                      },
                    ]}
                  >
                    <Input
                      size="large"
                      placeholder="Enter Line Item"
                      allowClear
                    />
                  </Form.Item>

                  <Form.Item
                    name="premiumValidTTSinglePGAmount"
                    label="Premium_Valid TT_Single PG Prm_Valid TT Amount"
                    rules={[
                      {
                        required: true,
                        message:
                          "Please enter Premium Valid TT Single PG Amount",
                      },
                      {
                        validator: (_, value) => {
                          if (value === undefined || value === null)
                            return Promise.resolve();
                          // Check if it has more than 2 decimals
                          const decimalPart = value.toString().split(".")[1];
                          if (decimalPart && decimalPart.length > 2) {
                            return Promise.reject(
                              new Error(
                                "Only up to 2 decimal places are allowed",
                              ),
                            );
                          }
                          return Promise.resolve();
                        },
                      },
                    ]}
                  >
                    <InputNumber
                      size="large"
                      placeholder="Enter Amount"
                      className="w-full"
                    />
                  </Form.Item>

                  <Form.Item
                    name="premiumValidTTDoublePGLineItem"
                    label="Premium_Valid TT_Double PG Prm_Line Item # 3"
                    rules={[
                      {
                        required: true,
                        message:
                          "Please enter Premium Valid TT Double PG Line Item",
                      },
                    ]}
                  >
                    <Input
                      size="large"
                      placeholder="Enter Line Item"
                      allowClear
                    />
                  </Form.Item>

                  <Form.Item
                    name="premiumValidTTDoublePGAmount"
                    label="Premium_Valid TT_Double PG Prm_Valid TT Amount"
                    rules={[
                      {
                        required: true,
                        message:
                          "Please enter Premium Valid TT Double PG Amount",
                      },
                      {
                        validator: (_, value) => {
                          if (value === undefined || value === null)
                            return Promise.resolve();
                          // Check if it has more than 2 decimals
                          const decimalPart = value.toString().split(".")[1];
                          if (decimalPart && decimalPart.length > 2) {
                            return Promise.reject(
                              new Error(
                                "Only up to 2 decimal places are allowed",
                              ),
                            );
                          }
                          return Promise.resolve();
                        },
                      },
                    ]}
                  >
                    <InputNumber
                      size="large"
                      placeholder="Enter Amount"
                      className="w-full"
                    />
                  </Form.Item>

                  <Form.Item
                    name="premiumPGRHLineItem"
                    label="Premium_PGRH Prm_Line Item # 4"
                    rules={[
                      {
                        required: true,
                        message: "Please enter Premium PGRH Line Item",
                      },
                    ]}
                  >
                    <Input
                      size="large"
                      placeholder="Enter Line Item"
                      allowClear
                    />
                  </Form.Item>

                  <Form.Item
                    name="premiumPGRHAmount"
                    label="Premium_PGRH Prm_PGRH Amount"
                    rules={[
                      {
                        required: true,
                        message: "Please enter Premium PGRH Amount",
                      },
                      {
                        validator: (_, value) => {
                          if (value === undefined || value === null)
                            return Promise.resolve();
                          // Check if it has more than 2 decimals
                          const decimalPart = value.toString().split(".")[1];
                          if (decimalPart && decimalPart.length > 2) {
                            return Promise.reject(
                              new Error(
                                "Only up to 2 decimal places are allowed",
                              ),
                            );
                          }
                          return Promise.resolve();
                        },
                      },
                    ]}
                  >
                    <InputNumber
                      size="large"
                      placeholder="Enter Amount"
                      className="w-full"
                    />
                  </Form.Item>
                </div>

                <div className="flex items-center gap-4 mb-5">
                  <div className="text-slate-600 text-lg font-semibold">
                    Contingency Information
                  </div>
                  <div className="flex-1 h-px bg-zinc-200"></div>
                </div>

                <div className="grid grid-cols-2 items-start gap-x-6 gap-y-0">
                  <Form.Item
                    name="contingencyMissedCallLineItem"
                    label="Contingency_Missed Call Con_Line Item # 1"
                    rules={[
                      {
                        required: true,
                        message:
                          "Please enter Contingency Missed Call Line Item",
                      },
                    ]}
                  >
                    <Input
                      size="large"
                      placeholder="Enter Line Item"
                      allowClear
                    />
                  </Form.Item>

                  <Form.Item
                    name="contingencyMissedCallAmount"
                    label="Contingency_Missed Call Con_Missed Call Amount"
                    rules={[
                      {
                        required: true,
                        message: "Please enter Contingency Missed Call Amount",
                      },
                      {
                        validator: (_, value) => {
                          if (value === undefined || value === null)
                            return Promise.resolve();
                          // Check if it has more than 2 decimals
                          const decimalPart = value.toString().split(".")[1];
                          if (decimalPart && decimalPart.length > 2) {
                            return Promise.reject(
                              new Error(
                                "Only up to 2 decimal places are allowed",
                              ),
                            );
                          }
                          return Promise.resolve();
                        },
                      },
                    ]}
                  >
                    <InputNumber
                      size="large"
                      placeholder="Enter Amount"
                      className="w-full"
                    />
                  </Form.Item>

                  <Form.Item
                    name="contingencyValidTTSinglePGLineItem"
                    label="Contingency_Valid TT_Single PG Con_Line Item # 2"
                    rules={[
                      {
                        required: true,
                        message:
                          "Please enter Contingency Valid TT Single PG Line Item",
                      },
                    ]}
                  >
                    <Input
                      size="large"
                      placeholder="Enter Line Item"
                      allowClear
                    />
                  </Form.Item>

                  <Form.Item
                    name="contingencyValidTTSinglePGAmount"
                    label="Contingency_Valid TT_Single PG Con_Valid TT Amount"
                    rules={[
                      {
                        required: true,
                        message:
                          "Please enter Contingency Valid TT Single PG Amount",
                      },
                      {
                        validator: (_, value) => {
                          if (value === undefined || value === null)
                            return Promise.resolve();
                          // Check if it has more than 2 decimals
                          const decimalPart = value.toString().split(".")[1];
                          if (decimalPart && decimalPart.length > 2) {
                            return Promise.reject(
                              new Error(
                                "Only up to 2 decimal places are allowed",
                              ),
                            );
                          }
                          return Promise.resolve();
                        },
                      },
                    ]}
                  >
                    <InputNumber
                      size="large"
                      placeholder="Enter Amount"
                      className="w-full"
                    />
                  </Form.Item>

                  <Form.Item
                    name="contingencyValidTTDoublePGLineItem"
                    label="Contingency_Valid TT_Double PG Con_Line Item # 3"
                    rules={[
                      {
                        required: true,
                        message:
                          "Please enter Contingency Valid TT Double PG Line Item",
                      },
                    ]}
                  >
                    <Input
                      size="large"
                      placeholder="Enter Line Item"
                      allowClear
                    />
                  </Form.Item>

                  <Form.Item
                    name="contingencyValidTTDoublePGAmount"
                    label="Contingency_Valid TT_Double PG Con_Valid TT Amount"
                    rules={[
                      {
                        required: true,
                        message:
                          "Please enter Contingency Valid TT Double PG Amount",
                      },
                      {
                        validator: (_, value) => {
                          if (value === undefined || value === null)
                            return Promise.resolve();
                          // Check if it has more than 2 decimals
                          const decimalPart = value.toString().split(".")[1];
                          if (decimalPart && decimalPart.length > 2) {
                            return Promise.reject(
                              new Error(
                                "Only up to 2 decimal places are allowed",
                              ),
                            );
                          }
                          return Promise.resolve();
                        },
                      },
                    ]}
                  >
                    <InputNumber
                      size="large"
                      placeholder="Enter Amount"
                      className="w-full"
                    />
                  </Form.Item>

                  <Form.Item
                    name="contingencyPGRHLineItem"
                    label="Contingency_PGRH Con_Line Item # 4"
                    rules={[
                      {
                        required: true,
                        message: "Please enter Contingency PGRH Line Item",
                      },
                    ]}
                  >
                    <Input
                      size="large"
                      placeholder="Enter Line Item"
                      allowClear
                    />
                  </Form.Item>

                  <Form.Item
                    name="contingencyPGRHAmount"
                    label="Contingency_PGRH Con_PGRH Amount"
                    rules={[
                      {
                        required: true,
                        message: "Please enter Contingency PGRH Amount",
                      },
                      {
                        validator: (_, value) => {
                          if (value === undefined || value === null)
                            return Promise.resolve();
                          // Check if it has more than 2 decimals
                          const decimalPart = value.toString().split(".")[1];
                          if (decimalPart && decimalPart.length > 2) {
                            return Promise.reject(
                              new Error(
                                "Only up to 2 decimal places are allowed",
                              ),
                            );
                          }
                          return Promise.resolve();
                        },
                      },
                    ]}
                  >
                    <InputNumber
                      size="large"
                      placeholder="Enter Amount"
                      className="w-full"
                    />
                  </Form.Item>
                </div>

                <div className="max-w-xl mx-auto pt-1 grid grid-cols-2 items-center gap-x-6 gap-y-0">
                  <Link
                    className="block w-full cancel_btn"
                    to="/np/price-matrix"
                  >
                    <Button block size="large">
                      Cancel
                    </Button>
                  </Link>
                  <Button
                    type="primary"
                    // disabled={disabledSubmit || disabled}
                    size="large"
                    htmlType="submit"
                    block
                    loading={updateLoading}
                  >
                    Update
                  </Button>
                </div>
              </Form>
            </div>
          )}
        </section>
      </AdminLayout>
    </>
  );
}
